import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../notification/notification.service';

// ============================================================================
// Anyagok — nyomtatási anyagok (filament) és csomagolóanyagok (doboz/címke)
// Design: erp-design/screens/19-anyagok.html + matEdit modál
// (21-kovetkezo-kepernyo.html), logika: 23-logic.js materialsVals/matEditVals.
// Adat: meglévő GET materials?filter= végpont.
// FIGYELEM: a backend Material entity jelenleg csak id+name mezőt tartalmaz,
// a POST/PATCH/DELETE materials végpontok pedig implementálatlan stubok —
// a UI kész, a hiányzó backend mezőket/műveleteket a záró jelentés rögzíti.
// ============================================================================

/** GET materials?filter= válasz egy eleme (a price/type/dims mezők a backend bővítése után érkeznek) */
interface MaterialDto {
  id?: number;
  name: string;
  /** 'filament' | 'box' — amíg a backend nem adja, minden anyag filamentként jelenik meg */
  type?: string;
  /** Ft/kg (filament) vagy Ft/db (doboz) */
  price?: number | string;
  /** méret / leírás (doboz) */
  dims?: string;
}

/** GET components?filter= — csak a material hivatkozás kell a felhasználás-számhoz */
interface ComponentRefDto {
  id?: number;
  material?: { id?: number } | null;
}

interface MaterialRow extends MaterialDto {
  usageCount: number;
  usageLabel: string;
}

type MaterialKind = 'filament' | 'box';

interface MaterialEditState {
  kind: MaterialKind;
  id: number | null;
  isNew: boolean;
  draft: { name: string; price: string; dims: string };
}

@Component({
  selector: 'app-anyagok',
  templateUrl: './anyagok.component.html',
  styleUrl: './anyagok.component.scss'
})
export class AnyagokComponent implements OnInit {
  filaments: MaterialRow[] = [];
  boxes: MaterialRow[] = [];
  loading = true;
  edit: MaterialEditState | null = null;
  saving = false;

  constructor(
    private http: HttpClient,
    private confirmDialog: ConfirmDialogService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      materials: this.http.get<MaterialDto[]>('materials?filter='),
      components: this.http.get<ComponentRefDto[]>('components?filter=').pipe(
        catchError(() => of([] as ComponentRefDto[])),
      ),
    }).subscribe({
      next: ({ materials, components }) => {
        const usage = new Map<number, number>();
        for (const c of components || []) {
          const materialId = c?.material?.id;
          if (materialId != null) {
            usage.set(materialId, (usage.get(materialId) || 0) + 1);
          }
        }
        const rows: MaterialRow[] = (Array.isArray(materials) ? materials : []).map(m => {
          const count = m.id != null ? (usage.get(m.id) || 0) : 0;
          return {
            ...m,
            usageCount: count,
            usageLabel: count > 0 ? count + ' termék használja' : 'Nincs termékhez kötve',
          };
        });
        this.filaments = rows.filter(m => m.type !== 'box');
        this.boxes = rows.filter(m => m.type === 'box');
        this.loading = false;
      },
      error: () => {
        this.filaments = [];
        this.boxes = [];
        this.loading = false;
        this.notification.show('Az anyagok betöltése nem sikerült.', 'danger');
      },
    });
  }

  /**
   * A backend DECIMAL(10,2)-t ad vissza, ami stringként „3200.00" formában
   * érkezik. Egész Ft-ra normalizáljuk — így a lista is olvasható, és a
   * szerkesztő mezőbe sem kerül tizedespont (amit az onPriceInput számjegy-
   * szűrője „320000"-re torzítana).
   */
  private normalizePrice(price: number | string | null | undefined): string {
    if (price === null || price === undefined || `${price}`.trim() === '') {
      return '';
    }
    const value = Number(price);
    if (!Number.isFinite(value)) {
      return '';
    }
    return `${Math.round(value)}`;
  }

  priceLabel(m: MaterialRow): string {
    const price = this.normalizePrice(m.price);
    return price === '' ? '—' : price;
  }

  // --------------------------------------------------------------------------
  // matEdit modál — 23-logic.js matEditVals 1:1
  // --------------------------------------------------------------------------

  openEdit(kind: MaterialKind, m: MaterialRow): void {
    this.edit = {
      kind,
      id: m.id ?? null,
      isNew: false,
      draft: {
        name: m.name || '',
        price: this.normalizePrice(m.price),
        dims: m.dims || '',
      },
    };
  }

  add(kind: MaterialKind): void {
    this.edit = { kind, id: null, isNew: true, draft: { name: '', price: '', dims: '' } };
  }

  get editKicker(): string {
    return this.edit ? (this.edit.isNew ? 'Új tétel' : 'Szerkesztés') : '';
  }

  get editTitle(): string {
    if (!this.edit) { return ''; }
    return (this.edit.isNew ? 'Új ' : '')
      + (this.edit.kind === 'filament' ? 'nyomtatási anyag' : 'csomagolóanyag');
  }

  get editUnit(): string {
    return this.edit && this.edit.kind === 'filament' ? 'Ft/kg' : 'Ft/db';
  }

  get editIdLabel(): string {
    return this.edit && this.edit.id != null ? '#' + this.edit.id : 'automatikus azonosító';
  }

  get editUsageCount(): number {
    if (!this.edit || this.edit.id == null) { return 0; }
    const id = this.edit.id;
    const row = this.filaments.find(f => f.id === id) || this.boxes.find(b => b.id === id);
    return row ? row.usageCount : 0;
  }

  get editUsageLabel(): string {
    return this.editUsageCount
      + ' termék használja ezt az anyagot. A név- és árváltozás automatikusan frissül mindegyiknél — a termékek nem esnek szét.';
  }

  get editValid(): boolean {
    if (!this.edit) { return false; }
    return !!this.edit.draft.name.trim() && !!this.edit.draft.price.trim();
  }

  onNameInput(event: Event): void {
    if (this.edit) {
      this.edit.draft.name = (event.target as HTMLInputElement).value;
    }
  }

  onDimsInput(event: Event): void {
    if (this.edit) {
      this.edit.draft.dims = (event.target as HTMLInputElement).value;
    }
  }

  onPriceInput(event: Event): void {
    if (!this.edit) { return; }
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/[^0-9]/g, '');
    input.value = digits;
    this.edit.draft.price = digits;
  }

  cancelEdit(): void {
    this.edit = null;
  }

  saveEdit(): void {
    if (!this.edit || !this.editValid || this.saving) { return; }
    const e = this.edit;
    const body: MaterialDto = {
      name: e.draft.name.trim(),
      type: e.kind,
      price: e.draft.price,
    };
    if (e.kind === 'box') {
      body.dims = e.draft.dims.trim();
    }
    const request$ = e.isNew
      ? this.http.post('materials', body)
      : this.http.patch(`materials/${e.id}`, body);
    this.saving = true;
    const wasNew = e.isNew;
    request$.subscribe({
      next: () => {
        this.saving = false;
        this.edit = null;
        this.notification.show(
          wasNew ? 'Az anyag létrejött.' : 'Az anyag mentve.',
          'success',
        );
        this.load();
      },
      error: () => {
        this.saving = false;
        this.notification.show('Az anyag mentése nem sikerült.', 'danger');
      },
    });
  }

  // --------------------------------------------------------------------------
  // Törlés — 23-logic.js remove handlerek (askConfirm) 1:1
  // --------------------------------------------------------------------------

  remove(kind: MaterialKind, m: MaterialRow): void {
    const isFilament = kind === 'filament';
    this.confirmDialog.open({
      title: 'Biztosan törlöd?',
      message: isFilament
        ? 'A(z) „' + m.name + '” anyag törlődik.'
        : 'A(z) „' + m.name + '” tétel törlődik.',
      details: isFilament && m.usageCount > 0
        ? m.usageCount + ' termék hivatkozik rá — ezeknél az önköltség újraszámol.'
        : undefined,
      tone: 'danger',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
    }).subscribe(result => {
      if (!result?.confirmed || m.id == null) { return; }
      this.http.delete(`materials/${m.id}`).subscribe({
        next: () => {
          this.notification.show('Az anyag törölve.', 'success');
          this.load();
        },
        error: () => this.notification.show('Az anyag törlése nem sikerült.', 'danger'),
      });
    });
  }
}
